import React from "react";
import { Trash2, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { IDS } from "@/constants/testIds";

const uid = () => Math.random().toString(36).slice(2, 10);

export default function LineItemsEditor({ items, onChange }) {
  const setItem = (i, patch) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () =>
    onChange([...items, { id: uid(), description: "", quantity: 1, unit_price: 0, unit: "" }]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-3 bg-[#F1F5F9] text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B]">
        <div className="col-span-6">Description</div>
        <div className="col-span-2 text-right">Qty</div>
        <div className="col-span-1 text-right">Unit</div>
        <div className="col-span-2 text-right">Price</div>
        <div className="col-span-1 text-right">Total</div>
      </div>
      <div className="divide-y divide-slate-100">
        {items.length === 0 && (
          <div className="p-6 text-center text-sm text-[#64748B]">
            No line items yet. Add one or use voice above.
          </div>
        )}
        {items.map((it, i) => {
          const total = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
          return (
            <div
              key={it.id || i}
              data-testid={IDS.editor.lineItem(i)}
              className="grid grid-cols-12 gap-2 sm:gap-3 px-3 sm:px-4 py-3 items-center"
            >
              {/* Row 1: Description (full width) */}
              <div className="col-span-12 sm:col-span-6">
                <Input
                  data-testid={IDS.editor.lineItemDesc(i)}
                  value={it.description}
                  onChange={(e) => setItem(i, { description: e.target.value })}
                  placeholder="Describe the item…"
                  className="border-slate-200 focus:border-[#2563EB] focus-visible:ring-blue-200"
                />
              </div>

              {/* Row 2 mobile: qty | unit | price | delete */}
              <div className="col-span-3 sm:col-span-2">
                <Input
                  data-testid={IDS.editor.lineItemQty(i)}
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  value={it.quantity}
                  onChange={(e) => setItem(i, { quantity: e.target.value })}
                  className="text-right border-slate-200"
                />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <Input
                  value={it.unit || ""}
                  onChange={(e) => setItem(i, { unit: e.target.value })}
                  placeholder="hr"
                  className="text-center border-slate-200 px-1"
                />
              </div>
              <div className="col-span-5 sm:col-span-2">
                <Input
                  data-testid={IDS.editor.lineItemPrice(i)}
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={it.unit_price}
                  onChange={(e) => setItem(i, { unit_price: e.target.value })}
                  className="text-right border-slate-200"
                />
              </div>

              {/* Total: hidden on mobile (redundant with grand total below), shown on desktop */}
              <div className="col-span-2 sm:col-span-1 flex items-center justify-end gap-2">
                <span className="hidden sm:inline text-sm font-semibold text-[#001F3F] tabular-nums">
                  {total.toFixed(2)}
                </span>
                <button
                  data-testid={IDS.editor.lineItemRemove(i)}
                  type="button"
                  onClick={() => remove(i)}
                  className="text-[#64748B] hover:text-[#EF4444] transition-colors p-1"
                  aria-label="Remove line item"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Mobile-only running total under the row */}
              <div className="col-span-12 sm:hidden -mt-1 text-xs text-[#64748B] flex justify-end tabular-nums">
                Line total: <span className="ml-1 font-semibold text-[#001F3F]">{total.toFixed(2)}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-3 border-t border-slate-100 bg-[#F8FAFC]">
        <Button
          data-testid={IDS.editor.addLineItem}
          type="button"
          variant="outline"
          onClick={add}
          className="w-full border-dashed border-slate-300 text-[#2563EB] hover:bg-blue-50 hover:text-[#1D4ED8]"
        >
          <Plus size={16} className="mr-2" /> Add line item
        </Button>
      </div>
    </div>
  );
}
